import { useState, type FormEvent } from 'react';
import { Info, Pencil } from 'lucide-react';
import {
  BoutonPrincipal,
  BoutonSecondaire,
  Carte,
  Chargement,
  MessageErreur,
  Pastille,
} from '../ui/Primitives';
import { useToast } from '../ui/useToast';
import { FacturesDuContrat } from '../financements/FacturesDuContrat';
import { EcheancierDuContrat } from '../financements/EcheancierDuContrat';
import { ApercuFactures, Rubrique } from '../financements/ApercuFactures';
import { AccordFinancementOf } from '../financements/AccordFinancementOf';
import { ErreurApi } from '../../lib/api';
import { detailsRefus } from '../../lib/erreurs-api';
import {
  LIBELLES_COUTS,
  centimesSaisis,
  formaterCentimes,
  montantEnSaisie,
  useEnregistrerPriseEnCharge,
  useFinancementDossier,
  type BlocFinancement,
  type CoutsPriseEnCharge,
  type FinancementDossier,
  type FinanceurChoisissable,
  type SaisiePriseEnCharge,
} from '../../lib/useFinancements';

const CLASSE_CHAMP =
  'min-h-[44px] w-full min-w-0 rounded-xl border border-gray-200 bg-gray-50 px-3 text-sm text-codialis-navy focus:border-codialis-sky focus:outline-none focus:ring-2 focus:ring-codialis-sky/20';

function jourFrancais(iso: string): string {
  const [annee, mois, jour] = iso.slice(0, 10).split('-');
  return `${jour}/${mois}/${annee}`;
}

function dateHeure(iso: string): string {
  return new Intl.DateTimeFormat('fr-FR', {
    timeZone: 'Indian/Reunion',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(new Date(iso));
}

function montantLu(valeur: string | null): string {
  if (valeur === null) return '—';
  return formaterCentimes(Math.round(Number(valeur) * 100));
}

/**
 * Bloc « Financement » du dossier apprenant (TF-404) : référence de prise en
 * charge (DK), coûts pris en charge et total calculé, **un bloc par contrat** —
 * l'OPCO attribue sa référence à chaque contrat.
 *
 * Réservé au pôle facturation et à la direction : l'API le refuse aux autres
 * pôles, et le dossier ne monte la carte que pour qui a l'écran « Financeurs &
 * OPCO ». Ce que le bloc dit — état, ce qui manque, total, financeur proposé —
 * vient de l'API ; la carte ne l'interprète pas.
 */
export function CarteFinancement({ apprenantId }: { apprenantId: string }): JSX.Element {
  const { data, isLoading, error } = useFinancementDossier(apprenantId);

  return (
    <Carte titre="Financement" actions={<Pastille texte="Pôle facturation" ton="info" />} sansBordure>
      {isLoading && <Chargement libelle="Lecture du financement…" />}
      {error && (
        <div className="p-4 sm:p-6">
          <MessageErreur
            message={
              error instanceof ErreurApi ? error.message : 'Le financement n’a pas pu être chargé.'
            }
          />
        </div>
      )}
      {/* Un parcours OF n'a pas de contrat d'apprentissage : son financement
          est un accord — contrat de professionnalisation ou France Travail —,
          facturé aux heures réalisées (TF-417), chaque mois pour un contrat de
          professionnalisation (TF-416). */}
      {data && data.mode === 'OF' && data.accord && (
        <AccordFinancementOf
          apprenantId={apprenantId}
          vue={data.accord}
          financeurs={data.financeurs}
          emetteur={data.emetteur}
          referentiel={data.referentielFacture}
        />
      )}
      {data && data.mode === 'CFA' && data.blocs.length === 0 && (
        <p className="p-4 text-sm text-codialis-gray sm:p-6">
          Aucun contrat validé : le financement se saisit sur le contrat d’apprentissage, une fois
          son CERFA généré.
        </p>
      )}
      {data && data.blocs.length > 0 && (
        <div className="divide-y divide-gray-100">
          {data.blocs.map((bloc) => (
            <BlocContrat
              key={bloc.contrat.id}
              bloc={bloc}
              financeurs={data.financeurs}
              emetteur={data.emetteur}
              referentiel={data.referentielFacture}
              referentielEcheancier={data.referentielEcheancier}
            />
          ))}
        </div>
      )}
    </Carte>
  );
}

function BlocContrat({
  bloc,
  financeurs,
  emetteur,
  referentiel,
  referentielEcheancier,
}: {
  bloc: BlocFinancement;
  financeurs: FinanceurChoisissable[];
  emetteur: FinancementDossier['emetteur'];
  referentiel: FinancementDossier['referentielFacture'];
  referentielEcheancier: FinancementDossier['referentielEcheancier'];
}): JSX.Element {
  const [edition, setEdition] = useState(false);
  const { contrat, priseEnCharge } = bloc;

  return (
    <section className="space-y-4 p-4 sm:p-6" aria-label={`Financement du contrat chez ${contrat.employeur.denomination}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h4 className="font-display break-words font-bold text-codialis-navy">
            {contrat.employeur.denomination}
          </h4>
          <p className="text-xs text-codialis-gray">
            Contrat du {jourFrancais(contrat.dateDebut)} au {jourFrancais(contrat.dateFin)}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Pastille texte={bloc.typeFacturation.libelle} ton="info" />
          <Pastille texte={contrat.situation.libelle} ton={contrat.situation.ton} />
          <Pastille texte={bloc.situation.libelle} ton={bloc.situation.ton} />
        </div>
      </div>

      {edition ? (
        <FormulairePriseEnCharge
          bloc={bloc}
          financeurs={financeurs}
          onFermer={() => setEdition(false)}
        />
      ) : (
        <>
          {bloc.situation.detail && (
            <p className="rounded-xl bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-800">
              {bloc.situation.detail}
            </p>
          )}
          <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Rubrique libelle="Type de facturation">
              {bloc.typeFacturation.libelle}
              <span className="block text-[11px] font-normal text-codialis-gray">
                {bloc.typeFacturation.facturation}
              </span>
            </Rubrique>
            <Rubrique libelle="Payeur">
              {priseEnCharge?.payeur === 'FINANCEUR'
                ? (priseEnCharge.financeur?.raisonSociale ?? '—')
                : priseEnCharge?.payeur === 'EMPLOYEUR'
                  ? `${contrat.employeur.denomination} (paiement direct)`
                  : 'Non renseigné'}
            </Rubrique>
            <Rubrique libelle="Référence de prise en charge (DK)">
              <span className="break-all font-mono">{priseEnCharge?.reference ?? '—'}</span>
            </Rubrique>
            <Rubrique libelle="N° de pro forma">
              <span className="break-all font-mono">{priseEnCharge?.numeroProForma ?? '—'}</span>
            </Rubrique>
            {contrat.numeroDepot && (
              <Rubrique libelle="N° de dépôt du CERFA">
                <span className="break-all font-mono">{contrat.numeroDepot}</span>
                {contrat.organismeDepot && (
                  <span className="block text-[11px] font-normal text-codialis-gray">
                    {contrat.organismeDepot}
                  </span>
                )}
              </Rubrique>
            )}
          </dl>

          <div className="rounded-2xl border border-gray-100">
            <ul className="divide-y divide-gray-100">
              {LIBELLES_COUTS.map(({ cle, libelle }) => (
                <li key={cle} className="flex items-center justify-between gap-3 px-3 py-2.5 text-sm sm:px-4">
                  <span className="min-w-0 text-codialis-gray">{libelle}</span>
                  <span className="shrink-0 font-medium tabular-nums text-codialis-navy">
                    {montantLu(priseEnCharge?.[cle] ?? null)}
                  </span>
                </li>
              ))}
            </ul>
            <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 rounded-b-2xl bg-codialis-sand px-3 py-3 sm:px-4">
              <span className="text-sm font-bold text-codialis-navy">Total pris en charge</span>
              <span className="font-display text-lg font-extrabold tabular-nums text-codialis-navy">
                {bloc.total.texte ?? '—'}
              </span>
              {bloc.total.addition && bloc.total.addition !== bloc.total.texte && (
                <span className="w-full text-right text-[11px] tabular-nums text-codialis-gray">
                  {bloc.total.addition}
                </span>
              )}
            </div>
          </div>

          {bloc.apercuFacturation && <ApercuFactures apercu={bloc.apercuFacturation} />}

          <EcheancierDuContrat bloc={bloc} referentiel={referentielEcheancier} />

          <FacturesDuContrat bloc={bloc} emetteur={emetteur} referentiel={referentiel} />

          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="min-w-0 text-[11px] text-codialis-gray">
              {priseEnCharge
                ? `Modifié le ${dateHeure(priseEnCharge.modifieLe)}${
                    priseEnCharge.modifiePar ? ` par ${priseEnCharge.modifiePar}` : ''
                  }.`
                : 'Aucun financement saisi pour ce contrat.'}
            </p>
            <BoutonSecondaire onClick={() => setEdition(true)}>
              <Pencil className="mr-1.5 h-4 w-4" aria-hidden />
              {priseEnCharge ? 'Modifier' : 'Saisir le financement'}
            </BoutonSecondaire>
          </div>
        </>
      )}
    </section>
  );
}

/** Valeur du choix du payeur : un identifiant de financeur, l'employeur, ou personne. */
const EMPLOYEUR = 'EMPLOYEUR';

function payeurInitial(bloc: BlocFinancement): string {
  const prise = bloc.priseEnCharge;
  if (prise?.payeur === 'FINANCEUR' && prise.financeur) return prise.financeur.id;
  if (prise?.payeur === 'EMPLOYEUR') return EMPLOYEUR;
  // Première saisie : l'OPCO de la fiche employeur, s'il est reconnu.
  if (!prise) return bloc.financeurPropose?.id ?? '';
  return '';
}

function FormulairePriseEnCharge({
  bloc,
  financeurs,
  onFermer,
}: {
  bloc: BlocFinancement;
  financeurs: FinanceurChoisissable[];
  onFermer: () => void;
}): JSX.Element {
  const toast = useToast();
  const enregistrer = useEnregistrerPriseEnCharge();
  const { contrat, priseEnCharge } = bloc;
  const [payeur, setPayeur] = useState(() => payeurInitial(bloc));
  const [reference, setReference] = useState(priseEnCharge?.reference ?? '');
  const [numeroProForma, setNumeroProForma] = useState(priseEnCharge?.numeroProForma ?? '');
  const [couts, setCouts] = useState<Record<keyof CoutsPriseEnCharge, string>>(() => ({
    coutPedagogique: montantEnSaisie(priseEnCharge?.coutPedagogique ?? null),
    fraisRestauration: montantEnSaisie(priseEnCharge?.fraisRestauration ?? null),
    fraisHebergement: montantEnSaisie(priseEnCharge?.fraisHebergement ?? null),
    fraisPremierEquipement: montantEnSaisie(priseEnCharge?.fraisPremierEquipement ?? null),
    fraisMobilite: montantEnSaisie(priseEnCharge?.fraisMobilite ?? null),
  }));
  const [erreur, setErreur] = useState<string | null>(null);

  const propose = !priseEnCharge && bloc.financeurPropose && payeur === bloc.financeurPropose.id;
  const idPrefixe = `financement-${contrat.id}`;

  // Aperçu du total pendant la saisie : l'API le recalcule à l'enregistrement.
  const lus = LIBELLES_COUTS.map(({ cle }) => centimesSaisis(couts[cle]));
  const illisible = lus.some((valeur) => valeur === undefined);
  const parts = lus.filter((valeur): valeur is number => typeof valeur === 'number');
  const total = parts.reduce((somme, valeur) => somme + valeur, 0);
  const visibles = parts.filter((valeur) => valeur !== 0);
  const apercu =
    illisible || parts.length === 0
      ? null
      : visibles.length > 1
        ? `${visibles.map(formaterCentimes).join(' + ')} = ${formaterCentimes(total)}`
        : formaterCentimes(total);

  const soumettre = (evenement: FormEvent): void => {
    evenement.preventDefault();
    if (illisible) {
      setErreur('Un montant s’écrit en euros, deux décimales au plus : 7 392,00.');
      return;
    }
    setErreur(null);
    const saisie: SaisiePriseEnCharge = {
      payeur:
        payeur === ''
          ? null
          : payeur === EMPLOYEUR
            ? { type: 'EMPLOYEUR' }
            : { type: 'FINANCEUR', financeurId: payeur },
      reference: reference.trim() || null,
      numeroProForma: numeroProForma.trim() || null,
      coutPedagogique: couts.coutPedagogique.trim() || null,
      fraisRestauration: couts.fraisRestauration.trim() || null,
      fraisHebergement: couts.fraisHebergement.trim() || null,
      fraisPremierEquipement: couts.fraisPremierEquipement.trim() || null,
      fraisMobilite: couts.fraisMobilite.trim() || null,
    };
    enregistrer.mutate(
      { contratId: contrat.id, saisie },
      {
        onSuccess: () => {
          toast(`Financement du contrat chez ${contrat.employeur.denomination} enregistré.`);
          onFermer();
        },
        onError: (refus) => {
          const detail = detailsRefus(refus)[0];
          setErreur(
            detail?.message ??
              (refus instanceof ErreurApi ? refus.message : 'Le financement n’a pas pu être enregistré.'),
          );
        },
      },
    );
  };

  return (
    <form onSubmit={soumettre} className="space-y-4" aria-label="Saisie du financement">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="min-w-0">
          <label htmlFor={`${idPrefixe}-payeur`} className="mb-1 block text-xs font-semibold text-codialis-navy">
            Payeur
          </label>
          <select
            id={`${idPrefixe}-payeur`}
            value={payeur}
            onChange={(e) => {
              setPayeur(e.target.value);
              setErreur(null);
            }}
            className={CLASSE_CHAMP}
          >
            <option value="">Non renseigné</option>
            <optgroup label="Financeurs">
              {financeurs.map((financeur) => (
                <option key={financeur.id} value={financeur.id}>
                  {financeur.raisonSociale}
                  {financeur.type === 'OPCO' ? '' : ` (${financeur.typeLibelle})`}
                </option>
              ))}
            </optgroup>
            <option value={EMPLOYEUR}>{contrat.employeur.denomination} (paiement direct)</option>
          </select>
          <p className="mt-1 text-[11px] text-codialis-gray">
            {propose
              ? `Proposé d’après l’OPCO de la fiche employeur : « ${contrat.opcoDeclare ?? ''} ».`
              : contrat.opcoDeclare
                ? `OPCO déclaré sur la fiche employeur : « ${contrat.opcoDeclare} ».`
                : 'Aucun OPCO déclaré sur la fiche employeur.'}
          </p>
        </div>
        <div className="min-w-0">
          <label
            htmlFor={`${idPrefixe}-reference`}
            className="mb-1 block text-xs font-semibold text-codialis-navy"
          >
            Référence de prise en charge (DK)
          </label>
          <input
            id={`${idPrefixe}-reference`}
            value={reference}
            onChange={(e) => {
              setReference(e.target.value);
              setErreur(null);
            }}
            maxLength={50}
            autoComplete="off"
            spellCheck={false}
            className={`${CLASSE_CHAMP} font-mono`}
          />
          <p className="mt-1 text-[11px] text-codialis-gray">
            {payeur === EMPLOYEUR
              ? 'Facultative quand l’employeur paie lui-même.'
              : 'Portée sur chaque facture adressée au financeur.'}
            {contrat.numeroDepot ? ` N° de dépôt du CERFA : ${contrat.numeroDepot}.` : ''}
          </p>
        </div>
        <div className="min-w-0">
          <label
            htmlFor={`${idPrefixe}-pro-forma`}
            className="mb-1 block text-xs font-semibold text-codialis-navy"
          >
            N° de pro forma
          </label>
          <input
            id={`${idPrefixe}-pro-forma`}
            value={numeroProForma}
            onChange={(e) => {
              setNumeroProForma(e.target.value);
              setErreur(null);
            }}
            maxLength={50}
            autoComplete="off"
            spellCheck={false}
            className={`${CLASSE_CHAMP} font-mono`}
          />
          <p className="mt-1 text-[11px] text-codialis-gray">
            Facultatif. Porté sur chaque facture, à côté de la référence de prise en charge.
          </p>
        </div>
      </div>

      <fieldset className="min-w-0">
        <legend className="mb-2 text-xs font-semibold text-codialis-navy">Coûts pris en charge</legend>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {LIBELLES_COUTS.map(({ cle, libelle }) => {
            const lu = centimesSaisis(couts[cle]);
            return (
              <div key={cle} className="min-w-0">
                <label
                  htmlFor={`${idPrefixe}-${cle}`}
                  className="mb-1 block text-[11px] font-semibold text-codialis-gray"
                >
                  {libelle}
                </label>
                <div className="relative">
                  <input
                    id={`${idPrefixe}-${cle}`}
                    value={couts[cle]}
                    onChange={(e) => {
                      setCouts((avant) => ({ ...avant, [cle]: e.target.value }));
                      setErreur(null);
                    }}
                    inputMode="decimal"
                    autoComplete="off"
                    aria-invalid={lu === undefined}
                    className={`${CLASSE_CHAMP} pr-8 text-right tabular-nums ${
                      lu === undefined ? 'border-red-300 bg-red-50' : ''
                    }`}
                  />
                  <span
                    aria-hidden
                    className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-sm text-codialis-gray"
                  >
                    €
                  </span>
                </div>
              </div>
            );
          })}
        </div>
        <p className="mt-2 flex gap-1.5 text-[11px] text-codialis-gray">
          <Info className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden />
          <span className="min-w-0">
            Un montant laissé vide est inconnu, pas nul. Le coût pédagogique est le total pris en charge pour
            toute la durée du contrat : chaque échéance en facture la part écoulée.
          </span>
        </p>
      </fieldset>

      <div
        className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 rounded-2xl bg-codialis-sand px-3 py-3 sm:px-4"
        aria-live="polite"
      >
        <span className="text-sm font-bold text-codialis-navy">Total pris en charge</span>
        <span className="font-display text-lg font-extrabold tabular-nums text-codialis-navy">
          {illisible ? 'Montant illisible' : parts.length === 0 ? '—' : formaterCentimes(total)}
        </span>
        {apercu && visibles.length > 1 && (
          <span className="w-full text-right text-[11px] tabular-nums text-codialis-gray">{apercu}</span>
        )}
      </div>

      {erreur && (
        <p role="alert" className="rounded-xl bg-red-50 px-3 py-2 text-xs font-semibold text-red-700">
          {erreur}
        </p>
      )}

      <div className="flex flex-wrap items-center justify-end gap-2">
        <BoutonSecondaire onClick={onFermer} disabled={enregistrer.isPending}>
          Annuler
        </BoutonSecondaire>
        <BoutonPrincipal type="submit" disabled={enregistrer.isPending}>
          {enregistrer.isPending ? 'Enregistrement…' : 'Enregistrer'}
        </BoutonPrincipal>
      </div>
    </form>
  );
}
